import React, { useEffect, useState } from 'react';

import { Button, ButtonGroup } from 'components/ui/button'
import { Modal } from 'components/ui/modal'

import { sharedActionCreators, SharedActionCreators } from 'containers/shared';
import { connect } from 'containers/store';

import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions';
import { GettyStore } from 'domains/org/getty/store';

import { EventPointOfInterest } from 'generated/mos/org/getty/audio'
import { Getty360Event } from 'generated/mos/org/getty/integration'
import { CreateEventPointOfInterestRequest } from 'generated/mos/org/getty/audiomanagement'

import { pick } from 'helpers/core'
import { Status } from 'helpers/status'

import { SearchEvents } from '../../events/search-events'
import { RadioInputElement } from 'components/ui/radio'
import { Description, Title, ListItemLabel, Subtitle } from 'components/ui/list-item-label'
import { calendarTitles } from '../helper'

type ListItemProps = {
  projectId: string;
  historyPush: (id: string) => void;
  event: Getty360Event.Entity;
};


type ConnectedProps = {
  eventPointOfInterestCreate: GettyStore['getty']['eventPointOfInterestCreate'];
};

type ActionProps =
  Pick<GettyActionCreators, 'gettyCreateEventPointOfInterestRequest' | 'gettyCreateEventPointOfInterestReset'> &
  Pick<SharedActionCreators, 'toastNotification'>;

type DirectProps = {
  onCancel: () => void;
  onComplete: (pointOfInterest: EventPointOfInterest.Entity) => void;
  moodRef: CreateEventPointOfInterestRequest.Entity['moodRef'];
  projectId: string;
  historyPush: (id: string) => void;
};

type Props = ConnectedProps & ActionProps & DirectProps;

const EventPointOfInterestCreateView = (props: Props) => {
  const {
    gettyCreateEventPointOfInterestRequest,
    gettyCreateEventPointOfInterestReset,
    onComplete,
    eventPointOfInterestCreate,
    toastNotification,
    projectId,
    historyPush,
  } = props

  const [ eventSelected, setEventSelected ] = useState<Getty360Event.Entity | undefined>()

  const onToggleEntity = (event: Getty360Event.Entity) => {
    const selectedRef = eventSelected && Getty360Event.isRef(eventSelected.ref) ? eventSelected.ref.id : undefined

    // it's currently selected, so unselect it.
    if (selectedRef && selectedRef === Getty360Event.mustRef(event.ref).id) {
      setEventSelected(Getty360Event.defaults)
    } else {
      // update the parent with the entity which has been selected.
      setEventSelected(event)
    }
  }

  const listItem = ({event}: ListItemProps) => {
    const selected =
      eventSelected &&
      eventSelected.ref &&
      Getty360Event.mustRef(eventSelected.ref).id === Getty360Event.mustRef(event.ref).id

    const calendars = calendarTitles(event)

    return (
      <ListItemLabel key={Getty360Event.mustRef(event.ref).id}>
        <RadioInputElement checked={selected} onChange={() => onToggleEntity(event)} name={Getty360Event.mustRef(event.ref).id}  />
        <Description>
          <Title>{event.title}</Title>
          <Subtitle>{calendars}{event.site} - {event.eventId}</Subtitle>
        </Description>
      </ListItemLabel>
    )
  }

  const createPointOfInterest = () => {
    if (eventSelected && eventPointOfInterestCreate.status === Status.Idle) {
      gettyCreateEventPointOfInterestRequest({
        ...CreateEventPointOfInterestRequest.defaults,
        type: CreateEventPointOfInterestRequest.refName,
        eventRef: eventSelected.ref,
        moodRef: props.moodRef
      })
    }

    if (eventPointOfInterestCreate.status === Status.Failed) {
      toastNotification({
        type: 'error',
        text: 'Could not create pointOfInterest',
      });
      gettyCreateEventPointOfInterestReset()
    }
  }

  useEffect(() => {
    if (eventPointOfInterestCreate.status === Status.Ready) {
      onComplete(eventPointOfInterestCreate.data);
      gettyCreateEventPointOfInterestReset();
    }
  }, [ eventPointOfInterestCreate.status ]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal
      onClose={props.onCancel}
      header={<h3>Find Event</h3>}
      footer={(
        <ButtonGroup>
          <Button onClick={props.onCancel} appearance="secondary">Cancel</Button>
          <Button isDisabled={!eventSelected} onClick={createPointOfInterest} >
            Create Point of Interest
          </Button>
        </ButtonGroup>
      )}
    >
      <SearchEvents projectId={projectId} historyPush={historyPush} listItem={listItem} />
    </Modal>
  );
};

export const EventPointOfInterestCreate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore>(
  store => ({
    eventPointOfInterestCreate: store.getty.eventPointOfInterestCreate
  }),
  {
    ...pick(actionCreators, 'gettyCreateEventPointOfInterestRequest', 'gettyCreateEventPointOfInterestReset'),
    ...pick(sharedActionCreators, 'toastNotification'),
  }
)(EventPointOfInterestCreateView)
